import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import * as riskService from "source/models/services/riskService";
import { List } from "source/components/framework/List/List";
import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { IRiskListItem, RiskListItem } from "ui/Scripts/source/models/risk/riskListItem";
import { RiskListLevelChip } from "../RiskList/RiskListLevelChip";
import { RiskListTraceabilityChip } from "../RiskList/RiskListTraceabilityChip";
import { getRiskPageGeneralFilterFromFilterState, getShowFilterCondition, IRiskPageFilterLists, IRiskPageFilterState } from "./RiskPageUtils";
import { RiskPageReportHtmlFilterCondition } from "./RiskPageReportHtmlFilterCondition";
import { IReportItem } from "source/models/report/reportItem";
import { IRiskSettingEdit } from "source/models/riskSetting/riskSettingEdit";
import { RiskPageReportHtmlContainer } from "./RiskPageReportHtmlContainer";
import { HtmlReportPanel } from "./HtmlReportPanel";

interface IRiskPageReportHtmlEmployeeRiskReportProp extends IRiskPageFilterLists, IRiskPageFilterState {
    companyId: string;
    settings: IRiskSettingEdit;
    report: IReportItem;
    dataId?: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    isPrinting: boolean;
    onSetInitialized: (initialized: boolean) => void;
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
}

interface IRiskPageReportHtmlEmployeeRiskReportState {
    intialized: boolean;
    pageSize: number;
    page: number;
    items: IRiskListItem[];
    //Visibilty options
    showFilterCondition: boolean;
}

export const RiskPageReportHtmlEmployeeRiskReport = (props: IRiskPageReportHtmlEmployeeRiskReportProp) => {
    const [state, setState] = useState<IRiskPageReportHtmlEmployeeRiskReportState>({
        intialized: false,
        pageSize: 10000,
        page: 1,
        items: [],
        showFilterCondition: false,
    });

    useEffect(() => {
        props.onSetInitialized(false);
    }, [props.companyId]);

    useEffect(() => {
        if (!props.report?.taskDataRow) return;
        setState(old => ({
            ...old,
            showFilterCondition: getShowFilterCondition(props.report),
        }));
    }, [props.report?.taskDataRow]);

    useEffect(() => {
        const loadData = async () => {
            const generalFilter = getRiskPageGeneralFilterFromFilterState(props);
            const result = await AppUtils.callService(() => riskService.getRiskListItems(props.companyId, state.pageSize, 1,
                false, null, generalFilter.typeIds, generalFilter.unitIds, generalFilter.stateIds, generalFilter.riskCategoryIds, generalFilter.riskProcessIds,
                generalFilter.riskStandardIds, generalFilter.riskStakeholderIds, generalFilter.residualRiskIds, generalFilter.riskTreatmentStateIds,
                generalFilter.handlerUserCodes, generalFilter.owners, props.sortColumn, props.sortOrderIsAsc), false);
            if (!result) return;
            setState((old) => ({
                ...old,
                intialized: true,
                items: result.items,
            }));
            props.onSetInitialized(true);
        };

        if (!props.companyId) return;
        loadData();
    }, [props.companyId,
        props.dataId,
        props.selectedRiskTypeIds.join(","),
        props.selectedUnitIds.join(","),
        props.selectedStateIds.join(","),
        props.selectedRiskCategoryIds.join(","),
        props.selectedRiskProcessIds.join(","),
        props.selectedRiskStandardIds.join(","),
        props.selectedRiskStakeholderIds.join(","),
        props.selectedResidualRiskIds.join(","),
        props.selectedRiskTreatmentStateIds.join(","),
        props.selectedHandlerUserCodes.join(","),
        props.selectedOwners.join(",")]);

    const sortList = (column: string, asc: boolean) => {
        const items = state.items.slice(0);
        RiskListItem.sortRiskListItemsByColumn(items, column, asc);
        props.onSetSortData(column, asc);
        setState(old => ({
            ...old,
            items
        }));
    };

    const handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, _nextPage: boolean) => {
        sortList(sortColumn, sortOrderIsAsc);
    };

    if (!state.intialized) return null;

    const getContent = (isPrinting: boolean) => {
        return (<>
            {state.showFilterCondition &&
                <RiskPageReportHtmlFilterCondition
                    isPrinting={isPrinting}
                    settings={props.settings}
                    {...props}
                />
            }
            <Box sx={{ marginTop: state.showFilterCondition ? 2 : 0 }}>
                <HtmlReportPanel
                    title={Translations.RiskRegisterSummary}
                    height={isPrinting ? "auto" : undefined}
                    isPrinting={isPrinting}
                >
                    <List
                        intialized={true}
                        isLoading={false}
                        hasMore={false}
                        height="auto"
                        header={{
                            columns: [
                                { size: 2, column: "title", content: Translations.Title },
                                { size: 2, column: "riskCategoryCode", content: Translations.RiskCategory },
                                { size: 3, column: "description", content: Translations.Description },
                                { size: 3, column: "controls", content: Translations.RiskControls },
                                { size: 2, column: "residualRisk", content: Translations.ResidualRisk }
                            ]
                        }}
                        lines={
                            state.items.map((item) => {
                                return {
                                    columns: [
                                        { size: 2, content: <Box>{item.title}</Box> },
                                        { size: 2, content: <Box>{item.riskCategoryCode}</Box> },
                                        { size: 3, content: <Box>{item.description}</Box> },
                                        { size: 3, content: <Box>{item.controls}</Box> },
                                        {
                                            size: 2,
                                            content: item.hasAssesment
                                                ? <Box>
                                                    <Box marginBottom=".5rem">
                                                        <RiskListLevelChip riskLevel={item.residualRisk} />
                                                    </Box>
                                                    <RiskListTraceabilityChip riskLevel={item.residualRisk} />
                                                </Box>
                                                : ""
                                        }
                                    ],
                                    id: item.id
                                };
                            })
                        }
                        sortColumn={props.sortColumn}
                        sortOrderIsAsc={props.sortOrderIsAsc}
                        selectedId={""}
                        checkedIds={[]}
                        onSetSelectedIds={() => {}}
                        onSearchLines={handleSearchLines}
                    />
                </HtmlReportPanel>
            </Box>
        </>
        );
    };

    return (
        <RiskPageReportHtmlContainer
            isPrinting={props.isPrinting}
            getContent={getContent}
        />
    );
};